import { useState } from "react"
import { Plus, X } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  Card,
  Input,
  Label,
  Badge,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from "@f2e/ui/components/base"

import {
  WebClientConfig,
  Workspace,
  ClientChannel,
  DescriptionItem,
} from "@/types"
import { useToast } from "@/hooks"
import { FetchStatus, useUploadClientChannelImageApi } from "@/hooks/api"
import { getUserFacingApiErrorToastProps } from "@/lib/apiErrors"

import {
  CLIENT_CHANNEL_IMAGE_ACCEPT,
  MAX_CLIENT_CHANNEL_IMAGE_SIZE_MB,
  isClientChannelImageTooLarge,
  isValidClientChannelImageType,
} from "./channelImageUpload"

export interface WebConfigFieldsProps {
  config: WebClientConfig
  isEditing: boolean
  workspace: Workspace
  channel: ClientChannel
  onConfigChange?: (config: Partial<WebClientConfig>) => void
}

export const WebConfigFields = ({
  config,
  isEditing,
  workspace,
  channel,
  onConfigChange,
}: WebConfigFieldsProps) => {
  const { t } = useTranslation()
  const { alertToast, successToast } = useToast()
  const [newAdButtonText, setNewAdButtonText] = useState("")
  const [newAdButtonUrl, setNewAdButtonUrl] = useState("")

  // APIs
  const { status: uploadImageStatus, fetchFn: uploadImage } =
    useUploadClientChannelImageApi(
      workspace.organizationId || "",
      workspace.id,
      channel.id
    )

  const isUploading = uploadImageStatus === FetchStatus.Loading
  const descriptions: DescriptionItem[] = config.descriptions || []

  const handleAddButton = () => {
    const text = newAdButtonText.trim()
    const url = newAdButtonUrl.trim()

    if (
      text &&
      url &&
      !config.adButtons.some(
        (button) => button.text === text && button.url === url
      )
    ) {
      onConfigChange?.({
        adButtons: [...config.adButtons, { text, url }],
      })
      setNewAdButtonText("")
      setNewAdButtonUrl("")
    }
  }

  const handleRemoveButton = (index: number) => {
    onConfigChange?.({
      adButtons: config.adButtons.filter((_, i) => i !== index),
    })
  }

  const handleImageUpload = async (
    slot: "login" | "header",
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!isValidClientChannelImageType(file)) {
      alertToast({
        title: t(
          "workspace.channels.config.web.imageUpload.invalidFileType.title",
          "Invalid file type"
        ),
        description: t(
          "workspace.channels.config.web.imageUpload.invalidFileType.description",
          "Please upload a PNG or JPEG image."
        ),
      })
      event.target.value = ""
      return
    }

    if (isClientChannelImageTooLarge(file)) {
      alertToast({
        title: t(
          "workspace.channels.config.web.imageUpload.fileSizeTooLarge.title",
          "File too large"
        ),
        description: t(
          "workspace.channels.config.web.imageUpload.fileSizeTooLarge.description",
          `Please upload an image smaller than ${MAX_CLIENT_CHANNEL_IMAGE_SIZE_MB}MB.`
        ),
      })
      event.target.value = ""
      return
    }

    const formData = new FormData()
    formData.append("slot", slot)
    formData.append("file", file)

    try {
      const resp = await uploadImage({ body: formData })
      const meta = resp?.data
      if (!meta || !meta.url) {
        alertToast({
          title: t(
            "workspace.channels.config.web.imageUpload.error.title",
            "Failed to upload image"
          ),
          description: t(
            "workspace.channels.config.web.imageUpload.error.description",
            "Please try again."
          ),
        })
        return
      }

      if (slot === "login") {
        onConfigChange?.({ loginImageUrl: meta.url })
      } else if (slot === "header") {
        onConfigChange?.({ headerImageUrl: meta.url })
      }

      successToast({
        title: t(
          "workspace.channels.config.web.imageUpload.success.title",
          "Image uploaded"
        ),
        description: t(
          "workspace.channels.config.web.imageUpload.success.description",
          "Your image has been uploaded successfully."
        ),
      })
    } catch (error) {
      console.error("Failed to upload client channel image:", error)
      alertToast({
        ...getUserFacingApiErrorToastProps(error, t, {
          title: t("workspace.channels.config.web.imageUpload.error.title"),
          fallbackDescription: t(
            "workspace.channels.config.web.imageUpload.error.description"
          ),
        }),
      })
    } finally {
      event.target.value = ""
    }
  }

  const handleDescriptionChange = (
    index: number,
    partial: Partial<DescriptionItem>
  ) => {
    const next = descriptions.map((item, i) =>
      i === index ? { ...item, ...partial } : item
    )
    onConfigChange?.({ descriptions: next })
  }

  const handleAddDescription = () => {
    const next: DescriptionItem[] = [
      ...descriptions,
      {
        type: "text",
        text: "",
        url: "",
      },
    ]
    onConfigChange?.({ descriptions: next })
  }

  const handleRemoveDescription = (index: number) => {
    const next = descriptions.filter((_, i) => i !== index)
    onConfigChange?.({ descriptions: next })
  }

  return (
    <div className="space-y-3">
      <Card className="p-6 space-y-6">
        {/* Editable fields (controlled by Edit button) */}
        {/* Title */}
        <div className="space-y-2">
          <Label htmlFor="config-title">
            {t("workspace.channels.config.web.title")}
          </Label>
          <Input
            id="config-title"
            value={config.title}
            onChange={(e) => onConfigChange?.({ title: e.target.value })}
            disabled={!isEditing}
            className="w-full"
          />
        </div>

        {/* Descriptions */}
        <div className="space-y-2">
          <Label>{t("workspace.channels.config.web.descriptions")}</Label>
          <p className="text-sm text-muted-foreground">
            {t("workspace.channels.config.web.descriptionsDescription")}
          </p>
          <div className="space-y-2">
            {descriptions.map((item, index) => (
              <div
                key={`${item.type}-${index}`}
                className="flex flex-col gap-2 md:flex-row md:items-start"
              >
                <div className="md:w-40">
                  <Select
                    value={item.type}
                    onValueChange={(value) =>
                      handleDescriptionChange(index, {
                        type: value as DescriptionItem["type"],
                      })
                    }
                    disabled={!isEditing}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="text">
                        {t("workspace.channels.config.web.descriptionTypeText")}
                      </SelectItem>
                      <SelectItem value="link">
                        {t("workspace.channels.config.web.descriptionTypeLink")}
                      </SelectItem>
                      <SelectItem value="email">
                        {t(
                          "workspace.channels.config.web.descriptionTypeEmail"
                        )}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1 flex flex-col gap-2 md:flex-row">
                  <Input
                    placeholder={t(
                      "workspace.channels.config.web.descriptionTextPlaceholder"
                    )}
                    value={item.text}
                    onChange={(e) =>
                      handleDescriptionChange(index, { text: e.target.value })
                    }
                    disabled={!isEditing}
                    className="flex-1"
                  />
                  {(item.type === "link" || item.type === "email") && (
                    <Input
                      placeholder={t(
                        "workspace.channels.config.web.descriptionUrlPlaceholder"
                      )}
                      value={item.url || ""}
                      onChange={(e) =>
                        handleDescriptionChange(index, {
                          url: e.target.value,
                        })
                      }
                      disabled={!isEditing}
                      className="flex-1"
                    />
                  )}
                </div>
                {isEditing && (
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => handleRemoveDescription(index)}
                    className="mt-1 md:mt-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
          {isEditing && (
            <Button
              type="button"
              variant="outline"
              onClick={handleAddDescription}
            >
              <Plus className="h-4 w-4 mr-2" />
              {t("workspace.channels.config.web.addDescription")}
            </Button>
          )}
        </div>

        {/* Redeem Code */}
        <div className="space-y-2">
          <Label htmlFor="config-valid-redeem-code">
            {t("workspace.channels.config.web.redeemPage")}
          </Label>
          <p className="text-sm text-muted-foreground">
            {t("workspace.channels.config.web.redeemPageDescription")}
          </p>
          <Input
            id="config-valid-redeem-code"
            value={config.validRedeemCode || ""}
            onChange={(e) =>
              onConfigChange?.({
                validRedeemCode: e.target.value,
              })
            }
            disabled={!isEditing}
            className="w-full"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="config-allow-anonymous-access">
            {t("workspace.channels.config.web.allowAnonymousAccess")}
          </Label>
          <div className="flex items-center gap-3">
            <Switch
              id="config-allow-anonymous-access"
              checked={config.allowAnonymousAccess || false}
              onCheckedChange={(checked) =>
                onConfigChange?.({ allowAnonymousAccess: checked })
              }
              disabled={!isEditing}
            />
            <p className="text-sm text-muted-foreground">
              {t(
                "workspace.channels.config.web.allowAnonymousAccessDescription"
              )}
            </p>
          </div>
          <p className="text-sm text-amber-600 dark:text-amber-500">
            {t("workspace.channels.config.web.allowAnonymousAccessWarning")}
          </p>
        </div>

        {/* Ad Buttons */}
        <div className="space-y-3">
          <Label>{t("workspace.channels.config.web.adButtons")}</Label>
          <div className="flex flex-wrap gap-2 min-h-[40px] p-3 border rounded-md">
            {config.adButtons.map((button, index) => (
              <Badge
                key={`${button.text}-${button.url}-${index}`}
                variant="secondary"
                className="px-3 py-1 flex items-center gap-2"
              >
                <span className="text-sm font-medium">{button.text}</span>
                <span className="text-xs text-muted-foreground">
                  {button.url}
                </span>
                {isEditing && (
                  <button
                    type="button"
                    className="inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground"
                    onClick={(event) => {
                      event.preventDefault()
                      event.stopPropagation()
                      handleRemoveButton(index)
                    }}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </Badge>
            ))}
          </div>
          {isEditing && (
            <div className="flex gap-2">
              <Input
                placeholder={t(
                  "workspace.channels.config.web.adButtonTextPlaceholder"
                )}
                value={newAdButtonText}
                onChange={(e) => setNewAdButtonText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    handleAddButton()
                  }
                }}
                className="flex-1"
              />
              <Input
                placeholder={t(
                  "workspace.channels.config.web.adButtonUrlPlaceholder"
                )}
                value={newAdButtonUrl}
                onChange={(e) => setNewAdButtonUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    handleAddButton()
                  }
                }}
                className="flex-1"
              />
              <Button size="icon" onClick={handleAddButton} variant="secondary">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </Card>

      <Card className="px-6">
        {/* Image upload section (independent of Edit button) */}
        <div className="space-y-4">
          <div className="py-3">
            {t("workspace.channels.config.web.images", "Channel images")}
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {/* Login Image */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">
                  {t("workspace.channels.config.web.loginImage", "Login image")}
                </span>
              </div>
              <div className="space-y-2">
                <div className="border rounded-md h-32 flex items-center justify-center overflow-hidden bg-muted">
                  {config.loginImageUrl ? (
                    <img
                      src={config.loginImageUrl}
                      alt="Login"
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <span className="text-sm text-muted-foreground">
                      {t(
                        "workspace.channels.config.web.loginImagePlaceholder",
                        "No login image uploaded"
                      )}
                    </span>
                  )}
                </div>
                <Input
                  type="file"
                  accept={CLIENT_CHANNEL_IMAGE_ACCEPT}
                  disabled={isUploading}
                  onChange={(e) => handleImageUpload("login", e)}
                />
                <p className="text-xs text-muted-foreground">
                  {t(
                    "workspace.channels.config.web.imageUpload.constraints",
                    `PNG or JPEG, max ${MAX_CLIENT_CHANNEL_IMAGE_SIZE_MB}MB`
                  )}
                </p>
              </div>
            </div>

            {/* Header Image */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">
                  {t(
                    "workspace.channels.config.web.headerImage",
                    "Header image"
                  )}
                </span>
              </div>
              <div className="space-y-2">
                <div className="border rounded-md h-32 flex items-center justify-center overflow-hidden bg-muted">
                  {config.headerImageUrl ? (
                    <img
                      src={config.headerImageUrl}
                      alt="Header"
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <span className="text-sm text-muted-foreground">
                      {t(
                        "workspace.channels.config.web.headerImagePlaceholder",
                        "No header image uploaded"
                      )}
                    </span>
                  )}
                </div>
                <Input
                  type="file"
                  accept={CLIENT_CHANNEL_IMAGE_ACCEPT}
                  disabled={isUploading}
                  onChange={(e) => handleImageUpload("header", e)}
                />
                <p className="text-xs text-muted-foreground">
                  {t(
                    "workspace.channels.config.web.imageUpload.constraints",
                    `PNG or JPEG, max ${MAX_CLIENT_CHANNEL_IMAGE_SIZE_MB}MB`
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}

export default WebConfigFields
